import styled from 'styled-components';

import Title from '@/components/Module/Title';
import useViewport from '@/hooks/useViewport';

export type PrivacyProps = {};

export const Paragraph = styled.div`
  padding-bottom: 2rem;
`;
export const ListItem = styled.li`
  padding-bottom: 2rem;
`;

const PrivacyContents = (props: PrivacyProps) => {
  const {} = props;

  return (
    <div className={`px-[24px]`}>
      <Paragraph>
        <Title>SOMEDAY.FM PRIVACY NOTICE</Title>
      </Paragraph>
      <Paragraph className={`mb-[24px] text-b2 font-normal text-gray-100`}>
        Last Updated: May 12, 2022
      </Paragraph>
      <Paragraph className={`text-b2 font-normal text-gray-200`}>
        <Paragraph>
          GigFinesse, Inc. dba Someday.fm (“Someday.fm,” “Someday,” “we,” “our,”
          or “us”) is committed to protecting your privacy. This Privacy Notice
          explains how your personal information is collected, used, and
          disclosed by Someday.fm. This Privacy Notice applies to our website,
          www.someday.fm (the “Site”) and other websites we own and operate that
          link to this Privacy Notice, and the related content, platform,
          services, products, and other functionality offered on or through our
          services (collectively, our “Service”).
        </Paragraph>
        <Paragraph>
          By accessing or using our Service, you signify that you have read,
          understood, and agree to our collection, storage, use, and disclosure
          of your personal information as described in this Privacy Notice and
          our{` `}
          <a href="https://www.someday.fm/terms">Terms of Service</a>. Please
          note that this Privacy Notice does not apply to our processing of
          information about employees or job applicants.
        </Paragraph>
        <h2>
          <strong>Region-Specific Disclosures</strong>
        </h2>
        <Paragraph>
          We may choose or be required by law to provide different or additional
          disclosures relating to the processing of personal information about
          residents of certain countries, regions, or states. Please refer below
          for disclosures that may be applicable to you:
        </Paragraph>
        <ul>
          <ListItem>
            If you are a resident of the State of California in the United
            States, California’s “Shine the Light” law (Civil Code Section §
            1798.83) permits California residents that have an established
            business relationship with us to request certain information
            regarding our disclosure of certain types of personal information to
            third parties for their direct marketing purposes during the
            immediately preceding calendar year. To make such a request, please
            contact us at support@someday.fm.
          </ListItem>
          <ListItem>
            If you are a resident of the State of Nevada in the United States,
            Chapter 603A of the Nevada Revised Statutes permits a Nevada
            resident to opt out of future sales of certain covered information
            that a website operator has collected or will collect about the
            resident. To submit such a request, please contact us at
            support@someday.fm.
          </ListItem>
        </ul>
        <h2>
          <strong>What is Personal Information?</strong>
        </h2>
        <Paragraph>
          When we use the term “personal information” in this Privacy Notice, we
          mean information that identifies, relates to, describes, is reasonably
          capable of being associated with, or could reasonably be linked,
          directly or indirectly, to you. It does not include aggregated or
          deidentified information that is maintained in a form that is not
          reasonably capable of being associated with or linked to you.
        </Paragraph>
        <h2>
          <strong>Our Collection of Personal Information</strong>
        </h2>
        <Paragraph>
          From the first moment you interact with us, we are collecting personal
          information about you. Sometimes we collect personal information
          automatically when you interact with our Service and sometimes, we
          collect the personal information directly from you. At times, we may
          collect personal information about you from other sources and third
          parties, even before our first direct interaction.
        </Paragraph>
        <Paragraph>
          <strong>
            <em>Personal Information You Provide</em>
          </strong>
        </Paragraph>
        <Paragraph>
          We collect the following personal information you provide in
          connection with our Service:
        </Paragraph>
        <ul>
          <ListItem>
            <strong>Account Registration and Profile Information:</strong> We
            collect personal information that you provide when you register for
            an account to use our Service. This may include your name, email
            address, phone number, address, payment information, date of birth
            and password, as well as your preferences for the ways in which we
            contact you for service-related inquiries (e.g., email or text
            messages). We use this information to administer your account,
            provide you with the relevant services and information, communicate
            with you regarding your account, the Service, and for customer
            support purposes. Please note that we use third party payment
            processors, including Stripe, to process credit card payments made
            to us and these processors may make available to us limited
            information such as the last four digits of your credit card,
            expiration date and your credit card type. The payment processor’s
            use of your personal information is governed by their privacy
            notice. To view Stripe’s privacy policy, please click{` `}
            <a href="https://stripe.com/privacy">here</a>.
          </ListItem>
          <ListItem>
            <strong>Inquiries and Other Communications:</strong> If you
            communicate with us through any form, we may collect your name,
            email address, mailing address, phone number, or any other personal
            information you choose to provide to us. We use this information to
            investigate and respond to your inquiries, and to communicate with
            you, to enhance the services we offer to our users and to manage and
            grow our organization.
          </ListItem>
          <ListItem>
            <strong>Other Data You Share</strong>: We also collect personal
            information at such other points on our Service that state that
            personal information is being collected so that we may provide the
            features or functionality for which we collected the personal
            information and for other purposes described in this Privacy Notice.
            We may also collect your personal information or data if you submit
            it to us in other contexts.
          </ListItem>
        </ul>
        <Paragraph>
          <strong>
            <em>Personal Information Automatically Collected</em>
          </strong>
        </Paragraph>
        <Paragraph>
          We use various technologies to automatically collect certain personal
          information about the device or browser you use to navigate our
          Service, including:
        </Paragraph>
        <ul>
          <ListItem>
            <strong>Web Logs</strong>: Our web servers keep log files that
            record data every time a device accesses those servers. As such,
            when you access this Website, we will automatically collect
            information such as your browser type and version, device and
            connection information, the Internet Protocol (IP) address used to
            connect your device to the Internet, and standard web log
            information.
          </ListItem>
          <ListItem>
            <strong>Location Information:</strong> We may collect general
            geographic location based on your IP address or more precise
            geolocation with your permission when accessing our online services
            through a mobile device.
          </ListItem>
          <ListItem>
            <strong>Cookies and Pixel Tags:</strong> We, and our third-party
            partners, automatically collect certain types of usage information
            when you visit our Service, read our emails, or otherwise engage
            with us. We typically collect this information through a variety of
            tracking technologies, including cookies, web beacons, embedded
            scripts, location-identifying technologies, file information, and
            similar technology (collectively, “tracking technologies”). For
            example, we collect information about your device and its software,
            such as your IP address, browser type, Internet service provider,
            platform type, device type, operating system, date and time stamp, a
            unique ID (that allows us to uniquely identify your browser, mobile
            device, or your account), and other such information. We also
            collect information about the way you use our Service, for example,
            the site from which you came and the site to which you are going
            when you leave our website, the pages you visit, the links you
            click, how frequently you access the Service, whether you open
            emails or click the links contained in emails, and other actions you
            take on the Service. Most internet browsers allow you to remove or
            manage cookie functions and adjust your privacy and security
            preferences. For information on how to do this, access the “help”
            menu on your internet browser, or access
            {` `}
            <a href="http://www.aboutcookies.org/how-to-control-cookies">
              http://www.aboutcookies.org/how-to-control-cookies
            </a>
            . Please note, however, that disabling our cookies may mean that you
            will not be able to take full advantage of the Service.
          </ListItem>
          <ListItem>
            <strong>Mobile Device Identifiers:</strong> When you access our
            Service from a mobile device, we may collect unique identification
            numbers associated with your device (including, for example, a UDID,
            Unique ID for Advertisers (“IDFA”), or Google AdID), mobile carrier,
            device type, model and manufacturer, mobile device operating system
            brand and model, phone number, and, depending on your mobile device
            settings, your geographical location data (with your permission),
            including GPS coordinates (e.g., latitude and/or longitude) or
            similar information regarding the location of your mobile device, or
            we may be able to approximate a device’s location by analyzing other
            information, like an IP address.
          </ListItem>
        </ul>
        <Paragraph>
          We use or may use the data collected through these technologies to:
          (a) remember information so that you will not have to re-enter it
          during your visit or the next time you visit the site; (b) provide
          custom, personalized content and information, including targeted
          content and advertising (see “Third Party Tracking and Online
          Advertising” below); © recognize and contact you across multiple
          devices; (d) provide and monitor the effectiveness of our Service; (e)
          monitor aggregate metrics such as total number of visitors, traffic,
          usage, and demographic patterns on our Service; (f) diagnose or fix
          technology problems; and (g) otherwise to plan for and enhance our
          Service.
        </Paragraph>
        <Paragraph>
          If you would prefer not to accept cookies, most browsers will allow
          you to: (i) change your browser settings to notify you when you
          receive a cookie, which lets you choose whether or not to accept it;
          (ii) disable existing cookies; or (iii) set your browser to
          automatically reject cookies. Please note that doing so may negatively
          impact your experience using the Service, as some features and
          services on our Service may not work properly. Depending on your
          mobile device and operating system, you may not be able to delete or
          block all cookies. You may also set your email options to prevent the
          automatic downloading of images that may contain technologies that
          would allow us to know whether you have accessed our email and
          performed certain functions with it.
        </Paragraph>
        <Paragraph>
          We and our third-party partners may also use cookies and tracking
          technologies for advertising purposes. For more information about
          tracking technologies, please see Third Party Tracking and Online
          Advertising below.
        </Paragraph>
        <Paragraph>
          <strong>
            <em>Personal Information from Third Party Sources</em>
          </strong>
        </Paragraph>
        <Paragraph>
          We also obtain personal information from other sources, which we often
          combine with personal information we collect either automatically or
          directly from you.
        </Paragraph>
        <Paragraph>
          We receive the same categories of personal information as described
          above from the following sources and third parties:
        </Paragraph>
        <ul>
          <ListItem>
            <strong>Single Sign-On</strong>: We may use single sign-on (“SSO”)
            to allow a user to authenticate their account using one set of login
            information, including through Google. We will have access to
            certain information from those third parties in accordance with the
            authorization procedures determined by those third parties, which
            may include, for example, your name, username, email address,
            language preference, and profile picture. We use this information to
            operate, maintain, and provide to you the features and functionality
            of the Service. We may also send you service-related emails or
            messages (e.g., account verification, customer support, changes, or
            updates to features of the Site, technical and security notices).
          </ListItem>
          <ListItem>
            <strong>Social Media</strong>: When an individual interacts with our
            Service through various social media networks, such as when someone
            “Likes” us on Facebook or follows us or shares our content on
            Google, Facebook, Twitter, or other social networks, we may receive
            some information about individuals that they permit the social
            network to share with third parties. The data we receive is
            dependent upon an individual’s privacy settings with the social
            network, and may include your profile information, profile picture,
            gender, username, user ID associated with your social media account,
            age range, language, country, and any other information you permit
            the social network to share with third parties. Individuals should
            always review and, if necessary, adjust their privacy settings on
            third-party websites and social media networks and services before
            sharing information and/or linking or connecting them to other
            services. We use this information to operate, maintain, and provide
            to you the features and functionality of the Service, as well as to
            communicate directly with you, such as to send you email messages
            about products and services that may be of interest to you.
          </ListItem>
          <ListItem>
            <strong>Service Providers</strong>: Our service providers that
            perform services solely on our behalf, such as analytics and certain
            marketing providers, collect personal information and often share
            some or all of this information with us. For example, we receive
            personal information you submit in response to requests for feedback
            to our feedback providers.
          </ListItem>
          <ListItem>
            <strong>Information from Other Sources:</strong> We may obtain
            information from other sources, including through third-party
            information providers, our shareholders, customers, or through
            transactions such as mergers and acquisitions. We may combine this
            information with other information we collect from or about you. In
            these cases, our Privacy Notice governs the handling of the combined
            personal information. We use this information to operate, maintain,
            and provide to you the features and functionality of the Service, as
            well as to communicate directly with you, such as to send you email
            messages about products and services that may be of interest to you.
          </ListItem>
        </ul>
        <h2>
          <strong>Our Use of Personal Information</strong>
        </h2>
        <Paragraph>We use personal information we collect to:</Paragraph>
        <ul>
          <ListItem>
            Set up your account and deliver the Service to you;
          </ListItem>
          <ListItem>
            Send you transactional information, including confirmations,
            invoices, technical notices, product and services information,
            updates, security alerts, support and administrative messages, and
            information about your transaction with us;
          </ListItem>
          <ListItem>
            Communicate with you such as to respond to your comments and
            questions, provide customer service or feedback, or respond to
            employment opportunities for which you’ve applied, or for any other
            purposes in connection with the Service;
          </ListItem>
          <ListItem>
            Provide you with news about products and services offered by us;
          </ListItem>
          <ListItem>
            Operate, maintain, test, enhance, update, monitor, diagnose, fix and
            improve our Service;
          </ListItem>
          <ListItem>
            Conduct research and analytics to understand our visitors and
            customers, and tailor our product offerings;
          </ListItem>
          <ListItem>
            Provide you with advertisements based on your interests and order
            history;
          </ListItem>
          <ListItem>
            Prevent, investigate or provide notice of fraud or unlawful or
            criminal activity, unauthorized access to or use of personal
            information, our data system services, and to meet governmental and
            institutional policy obligations;
          </ListItem>
          <ListItem>
            Enforce and carry out contracts and agreements with our customers;
          </ListItem>
          <ListItem>
            Investigate and resolve disputes and security issues and to enforce
            our{` `}
            <a href="https://www.someday.fm/terms">Terms of Service</a>;
          </ListItem>
          <ListItem>Comply with legal obligations; and</ListItem>
          <ListItem>
            For any other lawful, legitimate business purpose.
          </ListItem>
        </ul>
        <h2>
          <strong>Our Disclosure of Personal Information</strong>
        </h2>
        <Paragraph>
          We disclose personal information in the following ways:
        </Paragraph>
        <ul>
          <ListItem>
            <Paragraph>
              <strong>To Service Providers</strong>: We may share personal
              information with third-party service providers that perform
              services on our behalf, as needed to carry out their work for us,
              which may include email marketing and customer service providers.
              Depending on the function the third party serves, the service
              provider may process personal information on our behalf or have
              access to personal information while performing functions on our
              behalf.
            </Paragraph>
          </ListItem>
          <ListItem>
            <Paragraph>
              <strong>
                To Other Businesses as Needed to Provide the Service
              </strong>
              : We may share personal information with third parties that you
              engage with through our Service or as needed to fulfill a request
              or transaction including, for example, payment processing
              services.
            </Paragraph>
          </ListItem>
          <ListItem>
            <Paragraph>
              <strong>Advertisers and Publishers</strong>: We engage in
              advertising both on our Service and through third-party services.
              Advertisers and publishers may share personal information with us
              in connection with our advertising efforts. For example, we may
              obtain information about whether an advertisement on our Service
              led to a successful engagement between an you and a third-party
              advertiser.
            </Paragraph>
          </ListItem>
          <ListItem>
            <Paragraph>
              <strong>Business Transaction or Reorganization:</strong> We may
              take part in or be involved with a corporate business transaction,
              such as a merger, acquisition, joint venture, or financing or sale
              of company assets. We may disclose personal information to a
              third-party during negotiation of, in connection with or as an
              asset in such a corporate business transaction. Personal
              information may also be disclosed in the event of insolvency,
              bankruptcy, or receivership.
            </Paragraph>
          </ListItem>
          <ListItem>
            <Paragraph>
              <strong>Affiliates:</strong> We may share personal information
              with other companies owned or controlled by Someday.fm, and other
              companies owned by or under common ownership as Someday.fm, which
              also includes our subsidiaries (i.e., any organization we own or
              control) or our ultimate holding company (i.e., any organization
              that owns or controls us) and any subsidiaries it owns,
              particularly when we collaborate in providing the Services.
            </Paragraph>
          </ListItem>
          <ListItem>
            <Paragraph>
              <strong>Legal Obligations and Rights:</strong> We may disclose
              personal information to third parties, such as legal advisors and
              law enforcement:
            </Paragraph>
            <ul>
              <ListItem>
                <Paragraph>
                  in connection with the establishment, exercise, or defense of
                  legal claims;
                </Paragraph>
              </ListItem>
              <ListItem>
                <Paragraph>
                  to comply with laws or to respond to lawful requests and legal
                  process;
                </Paragraph>
              </ListItem>
              <ListItem>
                <Paragraph>
                  to protect the rights and property of us, our agents,
                  customers, and others, including to enforce our agreements,
                  policies, and terms of service;
                </Paragraph>
              </ListItem>
              <ListItem>
                <Paragraph>to detect, suppress, or prevent fraud;</Paragraph>
              </ListItem>
              <ListItem>
                <Paragraph>
                  to reduce credit risk and collect debts owed to us;
                </Paragraph>
              </ListItem>
              <ListItem>
                <Paragraph>
                  to protect the health and safety of us, our customers, or any
                  person; or
                </Paragraph>
              </ListItem>
              <ListItem>
                <Paragraph>as otherwise required by applicable law.</Paragraph>
              </ListItem>
            </ul>
          </ListItem>
          <ListItem>
            <Paragraph>
              <strong>Consent:</strong> We may disclose personal information
              about you to certain other third parties with your consent.
            </Paragraph>
          </ListItem>
        </ul>
        <h2>
          <strong>Third Party Tracking and Online Advertising</strong>
        </h2>
        <Paragraph>
          We participate in interest-based advertising and use third-party
          advertising companies to serve you targeted advertisements based on
          your online browsing history and your interests. We permit third-party
          online advertising networks, social media companies, and other
          third-party services to collect information about your use of our
          Service over time so that they may play or display ads on our Service,
          on other websites, apps, or services you may use, and on other devices
          you may use. Typically, though not always, the information used for
          interest-based advertising is collected through cookies or similar
          tracking technologies. We may share a common account identifier (such
          as an e-mail address or user ID) or hashed data with our third-party
          advertising partners to help identify you across devices. We and our
          third-party partners use this information to make the advertisements
          you see online more relevant to your interests, as well as to provide
          advertising-related services such as reporting, attribution,
          analytics, and market research.
        </Paragraph>
        <Paragraph>
          To learn more about interest-based advertising and how you may be able
          to opt out of some of this advertising, you may wish to visit the
          Digital Advertising Alliance’s (DAA) resources and/or the Network
          Advertising Initiative (“NAI”)’s online resources, at{` `}
          <a href="http://www.aboutads.info/choices">
            http://www.aboutads.info/choices
          </a>
          {` `}
          or{` `}
          <a href="http://www.networkadvertising.org/choices">
            http://www.networkadvertising.org/choices
          </a>
          , and you can opt out of receiving some interest-based advertisements
          on mobile apps by visiting{` `}
          <a href="http://www.aboutads.info/appchoices">
            http://www.aboutads.info/appchoices
          </a>
          . You may also be able to limit interest-based advertising through the
          settings menu on your mobile device by turning off “personalized ads”
          (iOS) or selecting “opt-out of interest-based ads” (Android). You may
          also be able to opt out of some – but not all – interest-based
          advertising served by mobile ad networks by visiting
          {` `}
          <a href="http://youradchoices.com/appchoices">
            http://youradchoices.com/appchoices
          </a>
          {` `}
          and downloading the mobile AppChoices app.
        </Paragraph>
        <Paragraph>
          Please note that some advertising opt-outs may not be effective unless
          your browser is set to accept cookies. Furthermore, if you use a
          different device, change browsers, or delete the opt-out cookies, you
          may need to perform the opt-out task again.
        </Paragraph>
        <Paragraph>
          <strong>
            <em>Google Analytics and Advertising: </em>
          </strong>
          We use Google Analytics to recognize you and link the devices you use
          when you visit our site or Service on your browser, log in to your
          account on our Service, or otherwise engage with us. We share a unique
          identifier, like a user ID or hashed e-mail address, with Google to
          facilitate the service. Google Analytics allows us to better
          understand how our users interact with our Service and to tailor our
          advertisements and content to you. For information on how Google
          Analytics collects and processes data, as well as how you can control
          information sent to Google, review Google’s site “How Google uses data
          when you use our partners’ sites or apps” located at{` `}
          <a href="http://www.google.com/policies/privacy/partners/">
            www.google.com/policies/privacy/partners/
          </a>
          . You can learn about Google Analytics’ currently available opt-outs,
          including the Google Analytics Browser Ad-On here{` `}
          <a href="https://tools.google.com/dlpage/gaoptout/">
            https://tools.google.com/dlpage/gaoptout/
          </a>
          .
        </Paragraph>
        <Paragraph>
          We may also utilize certain forms of display advertising and other
          advanced features through Google Analytics, such as Remarketing with
          Google Analytics, Google Display Network Impression Reporting, the
          DoubleClick Campaign Manager Integration, and Google Analytics
          Demographics and Interest Reporting. These features enable us to use
          first-party cookies (such as the Google Analytics cookie) and
          third-party cookies (such as the DoubleClick advertising cookie) or
          other third-party cookies together to inform, optimize, and display
          ads based on your past visits to the Service. You may control your
          advertising preferences or opt out of certain Google advertising
          products by visiting the Google Ads Preferences Manager, currently
          available at{` `}
          <a href="https://google.com/ads/preferences%2520">
            https://google.com/ads/preferences
          </a>
          , or by visiting NAI’s online resources at{` `}
          <a href="http://www.networkadvertising.org/choices">
            http://www.networkadvertising.org/choices
          </a>
          .
        </Paragraph>
        <h2>
          <strong>Control Over Your Information</strong>
        </h2>
        <ul>
          <ListItem>
            <strong>Modifying Account Information.</strong> If you have an
            account for our Service, you have the ability to modify certain
            information in your account (e.g., your contact information and
            communication preferences) through the account setting page or a
            similar option provided on the Service. If you have any questions
            about modifying or updating any information in your account, please
            contact us at support@someday.fm.
          </ListItem>
          <ListItem>
            <strong>
              How to Control Your Email Communications Preferences.
            </strong>
            {` `}
            You can stop receiving promotional email communications from us by
            clicking on the “unsubscribe link” provided in such communications
            or by adjusting your notification settings in the user settings
            page. We make every effort to promptly process all unsubscribe
            requests. You may not opt out of service-related communications
            (e.g., account verification, transactional communications,
            changes/updates to features of the Service, technical and security
            notices).
          </ListItem>
          <ListItem>
            <strong>How to Control Your SMS Text Messages Preferences.</strong>
            {` `}
            If you opted-in to our SMS text messaging program, you may opt-out
            of receiving text messages at any time. To unsubscribe from
            receiving SMS texts, please reply “STOP” to one of our SMS texts.
            You may also be able to change your SMS text preferences within your
            Someday.fm account settings. Note that if you unsubscribe from
            receiving text messages, we may still send you certain transactional
            text messages about your account or any services you have requested
            or receive from us (e.g., account verification, purchase
            confirmation or technical and security notices). For more
            information, please see our Terms of Service.
          </ListItem>
        </ul>
        <h2>
          <strong>Children’s Personal Information</strong>
        </h2>
        <Paragraph>
          Our Service are not directed to children under the age of 13. We do
          not knowingly collect or solicit personal information from children
          under the age of 13, and we do not intend to collect such information.
          If you are under the age of 13, please do not use our Service or
          otherwise provide us with any personal information either directly or
          by other means. If a child under the age of 13 has provided personal
          information to us, we encourage the child’s parent or guardian to
          contact us to request that we remove the personal information from our
          systems. If we learn that any personal information, we collect has
          been provided by a child under the age of 13, we will promptly delete
          that personal information.
        </Paragraph>
        <h2>
          <strong>Third-Party Websites</strong>
        </h2>
        <Paragraph>
          Our Service may include links to third-party websites, plug-ins, and
          applications. Except where we post, link to or expressly adopt or
          refer to this Privacy Notice, this Privacy Notice does not apply to,
          and we are not responsible for, any personal information practices of
          third-party websites and online services or the practices of other
          third parties. To learn about the personal information practices of
          third parties, please visit their respective privacy notices.
        </Paragraph>
        <h2>
          <strong>Updates to This Privacy Notice</strong>
        </h2>
        <Paragraph>
          We will update this Privacy Notice from time to time. When we make
          changes to this Privacy Notice, we will change the “Last Updated” date
          at the beginning of this Privacy Notice. If we make material changes
          to this Privacy Notice, we will notify you by email to your registered
          email address, by prominent posting on our Service, or through another
          reasonable method. All changes shall be effective from the date of
          publication unless otherwise provided.
        </Paragraph>
        <h2>
          <strong>Contact Us</strong>
        </h2>
        <Paragraph>
          If you have any questions or requests in connection with this Privacy
          Notice or other privacy-related matters, please send an email to
          support@someday.fm.
        </Paragraph>
      </Paragraph>
    </div>
  );
};

const MobilePrivacy = (props: PrivacyProps) => {
  const {} = props;

  return <PrivacyContents {...props} />;
};

const DesktopPrivacy = (props: PrivacyProps) => {
  const {} = props;

  return (
    <div className={`mx-auto flex w-[1200px]`}>
      <PrivacyContents {...props} />
    </div>
  );
};

const Privacy = (props: PrivacyProps) => {
  const {} = props;
  const viewport = useViewport();

  if (viewport == `mobile` || viewport == `tablet`) {
    return <MobilePrivacy {...props} />;
  }
  if (viewport == `desktop`) {
    return <DesktopPrivacy {...props} />;
  }

  return null;
};

export default Privacy;
